// Central JSON-LD builders. All pages compose from these so structured data
// stays consistent and easy to audit. Rendered via <SEOHead schema={...}/>
// and captured in the prerendered HTML at build time.

export const ORIGIN = "https://mvibeon.com";

export const MVIBE_ORG = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${ORIGIN}/#organization`,
  name: "MVIBE",
  alternateName: "MViBE Coaching & Training",
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/logo.png`,
  image: `${ORIGIN}/__l5e/assets-v1/0792666a-cef6-4e36-8bf0-b54d797bf654/og-image-mvibe.jpg`,
  description:
    "MVIBE delivers ICF-aligned executive coaching, corporate soft skills training, and campus-to-corporate programmes across Dubai, the UAE and India.",
  founder: { "@id": `${ORIGIN}/about#mahirah` },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Building 12, Bawabat Al Sharq",
    addressLocality: "Baniyas",
    addressRegion: "Abu Dhabi",
    addressCountry: "AE",
  },
  areaServed: [
    { "@type": "City", name: "Dubai" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "India" },
  ],
  sameAs: [
    "https://www.linkedin.com/company/mvibeon-training-coaching/",
    "https://www.instagram.com/mvibe_on",
    "https://twitter.com/mvibeon",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "mvibeon@gmail.com",
      telephone: "+91-78998-04670",
      areaServed: "IN",
      availableLanguage: ["en"],
    },
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "mvibeon@gmail.com",
      telephone: "+971-52-540-9896",
      areaServed: "AE",
      availableLanguage: ["en"],
    },
  ],
};

export const MAHIRAH_PERSON = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${ORIGIN}/about#mahirah`,
  name: "Mahirah",
  alternateName: "Sana Amrin",
  jobTitle: "Founder & Executive Coach",
  worksFor: { "@id": `${ORIGIN}/#organization` },
  url: `${ORIGIN}/about`,
  image: `${ORIGIN}/__l5e/assets-v1/0792666a-cef6-4e36-8bf0-b54d797bf654/og-image-mvibe.jpg`,
  sameAs: ["https://www.linkedin.com/company/mvibeon-training-coaching/"],
  knowsAbout: [
    "executive coaching",
    "emotional intelligence",
    "leadership training",
    "soft skills training",
    "communication skills",
    "campus to corporate",
    "personal branding",
  ],
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "ICF Aligned Coach" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "NLP Practitioner" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "ART Therapy Practitioner" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "NABET Certified Soft Skills Trainer" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Certified POSH Trainer" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "City & Guilds Certified" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Certified Graphologist" },
  ],
};

export const buildBreadcrumb = (crumbs: Array<{ name: string; path: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${ORIGIN}${c.path === "/" ? "/" : c.path.replace(/\/+$/, "")}`,
  })),
});

export const buildService = (params: {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: params.name,
  description: params.description,
  serviceType: params.serviceType ?? params.name,
  url: `${ORIGIN}${params.path}`,
  provider: { "@id": `${ORIGIN}/#organization` },
  areaServed: [
    { "@type": "City", name: "Dubai" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "India" },
  ],
});

export const buildFAQ = (qas: Array<{ q: string; a: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qas.map((qa) => ({
    "@type": "Question",
    name: qa.q,
    acceptedAnswer: { "@type": "Answer", text: qa.a },
  })),
});

export const buildWebPage = (params: {
  name: string;
  description: string;
  path: string;
  type?: "WebPage" | "CollectionPage" | "ContactPage" | "AboutPage";
}) => ({
  "@context": "https://schema.org",
  "@type": params.type ?? "WebPage",
  name: params.name,
  description: params.description,
  url: `${ORIGIN}${params.path === "/" ? "/" : params.path.replace(/\/+$/, "")}`,
  isPartOf: { "@id": `${ORIGIN}/#organization` },
});

// Common FAQ set reused on Home + service pages (edited/subset per page).
export const CORE_FAQS: Array<{ q: string; a: string }> = [
  {
    q: "What does MVIBE offer?",
    a: "MVIBE offers ICF-aligned executive coaching, corporate soft skills training, 1:1 life coaching, campus-to-corporate programmes, personal branding and image consulting. Programmes run in Dubai, across the UAE and in India, delivered in-person or virtually to leaders, teams, students and working professionals.",
  },
  {
    q: "Who is Mahirah, the founder of MVIBE?",
    a: "Mahirah (Sana Amrin) is the founder and lead coach at MVIBE. She is an ICF-aligned Executive Coach with 15+ years of IT HR leadership experience, credentialed in NLP, ART Therapy, Graphology, POSH, NABET soft skills and City & Guilds - a rare blend of corporate depth and human-first coaching.",
  },
  {
    q: "How does the discovery call and pricing work?",
    a: "Every engagement starts with a free 30-minute discovery call to understand your goals and recommend the right programme. Coaching packages, corporate training and student programmes are quoted after this call so pricing reflects scope, format and audience rather than a one-size template.",
  },
  {
    q: "Do you deliver online, in-person or both?",
    a: "Both. 1:1 coaching runs primarily online for flexibility. Corporate training and offsites are delivered in-person in Dubai and Bangalore, and virtually anywhere in the world. Campus-to-corporate workshops run on-site at colleges or as blended cohorts, depending on the institution.",
  },
  {
    q: "Which locations does MVIBE serve?",
    a: "MVIBE serves clients across the United Arab Emirates (Dubai, Abu Dhabi) and India (Bangalore and pan-India), plus international clients virtually. Corporate engagements have included tech firms, consulting groups, universities and government-linked organisations across both regions.",
  },
  {
    q: "What makes MVIBE different from other coaching firms?",
    a: "Three things: an ICF-aligned coach with 15+ years of real IT HR leadership behind her, integrated tools you rarely see combined (ART Therapy, Graphology, NLP, image consulting) and programmes built for real corporate pressure, not textbook theory. Every engagement is tailored, never templated.",
  },
  {
    q: "How do I get started?",
    a: "Book a free discovery call via the Contact page or the floating enquiry button. Share your goals, current context and preferred format. Within two working days you will receive a tailored proposal outlining recommended programme, duration, delivery mode and investment.",
  },
];
